/**
 * Detects a selected slot becoming unavailable after the user leaves the Time step.
 * Keeps the established inline Time-step banner while adding cross-step protection.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { RmcOrderIntakeStepType } from '../../config/stepTypes'
import { SlotValidityModal } from '../../../shared/order-editor/components/slotValidity/SlotValidityModal'
import { useSlotManagement } from '../../../shared/order-editor/components/fields/Time/useSlotManagement'

export interface OrderIntakeSlotValidityCheckerProps {
    currentStepKey: string
    onChangeStartTime: () => void
}

export const OrderIntakeSlotValidityChecker: React.FC<OrderIntakeSlotValidityCheckerProps> = ({
    currentStepKey,
    onChangeStartTime
}) => {
    const [isSlotUnavailable, setIsSlotUnavailable] = useState(false)
    const previousAvailability = useRef<boolean | undefined>(undefined)
    const slotManagementOptions = useSlotManagement()
    const isOnTimeStep = currentStepKey === RmcOrderIntakeStepType.TIME
    const isAvailabilitySettled =
        slotManagementOptions.slotManagementEnabled &&
        !slotManagementOptions.isLoading &&
        !slotManagementOptions.isFetching

    useEffect(() => {
        if (!isAvailabilitySettled) {
            return
        }

        const becameUnavailable =
            previousAvailability.current === true && !slotManagementOptions.isSelectedSlotAvailable

        previousAvailability.current = slotManagementOptions.isSelectedSlotAvailable

        if (!isOnTimeStep && becameUnavailable) {
            setIsSlotUnavailable(true)
        }
    }, [isAvailabilitySettled, isOnTimeStep, slotManagementOptions.isSelectedSlotAvailable])

    useEffect(() => {
        if (isOnTimeStep) {
            setIsSlotUnavailable(false)
        }
    }, [isOnTimeStep])

    const handleChangeStartTime = useCallback(() => {
        setIsSlotUnavailable(false)
        onChangeStartTime()
    }, [onChangeStartTime])

    const handleKeepStartTime = useCallback(() => {
        setIsSlotUnavailable(false)
    }, [])

    return (
        <SlotValidityModal
            isVisible={isSlotUnavailable}
            onChangeStartTime={handleChangeStartTime}
            onKeepStartTime={handleKeepStartTime}
        />
    )
}